<script setup lang="ts">
import { computed, ref, watch, inject, nextTick } from 'vue'
import { Combobox, createListCollection } from '@ark-ui/vue'
import UiText from '../UiText.vue'
import UiAsyncComboboxControl from './UiAsyncComboboxControl.vue'
import UiAsyncComboboxContent from './UiAsyncComboboxContent.vue'
import UiAsyncComboboxChips from './UiAsyncComboboxChips.vue'
import type { ComboboxItemData, ComboboxGroupData } from '../types/combobox'
import { watchEffect } from 'vue'

export interface UiAsyncComboboxProps {
    id?: string
    name?: string
    placeholder?: string
    disabled?: boolean
    size?: 'md' | 'lg'
    /**
     * Controls input and selection behavior:
     * - 'preserve': Keeps search text after selection, auto-manages selectedId (default, best for multi-select)
     * - 'clearOnSelect': Clears input after selection, doesn't auto-manage selectedId (traditional autocomplete)
     */
    mode?: 'preserve' | 'clearOnSelect'
    displayMode?: 'inline' | 'combobox'
    hideComboboxWhenEmpty?: boolean
    multiple?: boolean
    data: ComboboxGroupData[] | ComboboxItemData[]
    isFetching: boolean
    isError?: boolean
    error?: Error | string | null
    invalid?: boolean
    hint?: string
    onValidate?: () => void
    forceSelectFirst?: boolean
    showSelectedImage?: boolean
    /** More results exist beyond `data`; scrolling near the end of the list emits `load-more`. */
    hasMore?: boolean
}

const props = withDefaults(defineProps<UiAsyncComboboxProps>(), {
    size: 'lg',
    placeholder: 'Search...',
    invalid: false,
    mode: 'preserve',
    displayMode: 'inline',
    hideComboboxWhenEmpty: false,
    multiple: false,
    forceSelectFirst: false,
    showSelectedImage: false,
    hasMore: false,
})

const inputValue = defineModel<string>()
const selectedId = defineModel<string | string[] | undefined>('selectedId', { required: false })
const selectedItems = defineModel<ComboboxItemData[] | undefined>('selectedItems', { required: false })

const comboboxValue = ref<string[]>([])
const isSelectingItem = ref(false)

const emit = defineEmits<{
    (e: 'load-more'): void
    (e: 'select', value: { value: string | string[]; label: string | string[]; metadata?: Record<string, unknown> | Record<string, unknown>[] }): void
    (e: 'validate'): void
    (e: 'blur', context: {
        items: ComboboxItemData[]
        selectItem: (item: ComboboxItemData) => void
        selectItems: (items: ComboboxItemData[]) => void
        currentText: string | undefined
        selectedId: string | string[] | undefined
    }): void
}>()

const form = inject<any>('UiForm:form', null)
const isOpen = ref(false)

function isGroupedData(data: ComboboxGroupData[] | ComboboxItemData[]): data is ComboboxGroupData[] {
    if (!data || data.length === 0) return false
    return 'items' in data[0]
}

// Note: We no longer clear selections when data changes - this preserves
// selected items in multi-select mode when the search query changes

const isGrouped = computed(() => {
    const data = props.data;
    if (!data || data.length === 0) return false
    return isGroupedData(data)
})

const groupedData = computed<ComboboxGroupData[]>(() => {
    const data = props.data;
    if (!data || data.length === 0) return []
    
    if (isGroupedData(data)) {
        return data
    }
    
    return [{
        id: '__default__',
        label: '',
        items: data as ComboboxItemData[],
        showImage: false
    }]
})

const allItems = computed(() => {
    const items: ComboboxItemData[] = []
    groupedData.value.forEach(group => {
        items.push(...group.items)
    })
    // Include selected items that aren't in the current search results
    // This ensures Ark UI can still track and display them
    if (selectedItems.value && selectedItems.value.length > 0) {
        const existingValues = new Set(items.map(i => i.value))
        for (const selected of selectedItems.value) {
            if (!existingValues.has(selected.value)) {
                items.push(selected)
            }
        }
    }
    return items
})

const collection = computed(() => createListCollection({
    items: allItems.value,
    itemToValue: (item: ComboboxItemData) => item.value,
    itemToString: (item: ComboboxItemData) => item.label,
}))

function getLabel(value: string): string {
    const item = allItems.value.find(i => i.value === value)
    return item?.label || value
}

function getGroupItems(groupId: string): ComboboxItemData[] {
    const group = groupedData.value.find(g => g.id === groupId)
    return group ? group.items : []
}

function getLinkProps(link: string | { href: string; label: string } | undefined) {
    if (!link) return { href: undefined, label: undefined }
    if (typeof link === 'string') return { href: link, label: 'View All' }
    return { href: link.href, label: link.label }
}

const hasResults = computed(() => allItems.value.length > 0)
const isErrorValue = computed(() => props.isError ? props.isError : false)

const formError = computed(() => {
    if (props.error) {
        const error = props.error;
        if (!error) return undefined
        return error instanceof Error ? error.message : String(error)
    }
    return undefined
})

const validationError = computed(() => formError.value)
const isInvalid = computed(() => props.invalid || Boolean(validationError.value))

const isDisabled = computed(() => {
    if (props.disabled) return true
    if (form?.meta?.isSubmitting) return true
    return false
})

const describedByIdBase = computed(() => props.id || props.name)

const openOnClick = computed(() => props.displayMode === 'combobox')
const openOnKeyPress = computed(() => props.displayMode === 'inline')

function getLabelForValue(value: string): string {
    const selectedItem = selectedItems.value?.find(item => item.value === value)
    if (selectedItem) {
        return selectedItem.label
    }
    return getLabel(value)
}

function clearInput() {
    isOpen.value = false
    inputValue.value = ''
    comboboxValue.value = []
    selectedItems.value = []
    selectedId.value = undefined
    if (props.onValidate) {
        props.onValidate()
    } else {
        emit('validate')
    }
}

function removeItem(value: string) {
    comboboxValue.value = comboboxValue.value.filter(v => v !== value)
    selectedItems.value = selectedItems.value?.filter(item => item.value !== value)
}

function handleMultiSelectValueChange(details: { value: string[]; items: ComboboxItemData[] }) {
    if (!details.value || details.value.length === 0) {
        selectedItems.value = []
        if (props.mode === 'preserve') {
            selectedId.value = []
        }
        return
    }
    
    // Merge items from Ark UI with previously selected items
    // This ensures we don't lose item data when items aren't in current search results
    const existingItemsMap = new Map<string, ComboboxItemData>()
    if (selectedItems.value) {
        for (const item of selectedItems.value) {
            existingItemsMap.set(item.value, item)
        }
    }
    
    // Build the new selected items array, preserving full item data
    const newSelectedItems: ComboboxItemData[] = []
    for (const value of details.value) {
        // First try to get from the items Ark UI provided
        const arkItem = details.items.find(i => i.value === value)
        if (arkItem && arkItem.label) {
            newSelectedItems.push(arkItem)
            existingItemsMap.set(value, arkItem) // Update cache
        } else {
            // Fall back to previously cached item
            const cached = existingItemsMap.get(value)
            if (cached) {
                newSelectedItems.push(cached)
            }
        }
    }
    
    selectedItems.value = newSelectedItems

    isSelectingItem.value = true
    emit('select', {
        value: newSelectedItems.map(item => item.value),
        label: newSelectedItems.map(item => item.label),
        metadata: newSelectedItems.map(item => item.metadata).filter((m): m is Record<string, unknown> => m !== undefined)
    })
    if (props.mode === 'preserve') {
        selectedId.value = newSelectedItems.map(item => item.value)
    }
    setTimeout(() => {
        isSelectingItem.value = false
    }, 0)
}

function handleSingleSelectValueChange(details: { value: string; item: ComboboxItemData }) {
    console.log('handleSingleSelectValueChange', details)
    if (details.value && details.item) {
        isSelectingItem.value = true
        selectedId.value = details.item.value
        selectedItems.value = [details.item]
        emit('select', {
            value: details.item.value,
            label: details.item.label,
            metadata: details.item.metadata
        })
        if (props.mode === 'preserve') {
            inputValue.value = details.item.label
        }
        setTimeout(() => {
            isSelectingItem.value = false
        }, 0)
    } else {
        selectedItems.value = []
        if (props.mode === 'preserve') {
            selectedId.value = undefined
        }
    }
}
    

function handleValueChange(details: { value: string[]; items: ComboboxItemData[] }) {
    if (props.multiple) {
        handleMultiSelectValueChange(details)
    } else {
        handleSingleSelectValueChange({ value: details.value[0], item: details.items[0] })
    }
}

function selectItem(item: ComboboxItemData) {
    if (props.multiple) {
        const currentValues = Array.isArray(selectedId.value) ? selectedId.value : (selectedId.value ? [selectedId.value] : [])
        if (currentValues.includes(item.value)) return
        
        const newValues = [...currentValues, item.value]
        const newItems = [...(selectedItems.value || []), item]
        comboboxValue.value = newValues
        handleMultiSelectValueChange({ value: newValues, items: newItems })
    } else {
        comboboxValue.value = [item.value]
        handleSingleSelectValueChange({ value: item.value, item })
    }
}

function selectItems(items: ComboboxItemData[]) {
    if (props.multiple) {
        const newValues = items.map(item => item.value)
        comboboxValue.value = newValues
        handleMultiSelectValueChange({ value: newValues, items })
    } else if (items.length > 0) {
        comboboxValue.value = [items[0].value]
        handleSingleSelectValueChange({ value: items[0].value, item: items[0] })
    }
}

function handleBlur() {
    const blurContext = {
        items: allItems.value,
        selectItem,
        selectItems,
        currentText: inputValue.value,
        selectedId: selectedId.value,
    }
    
    if (props.forceSelectFirst && !selectedId.value && allItems.value.length > 0) {
        selectItem(allItems.value[0])
    }
    if (selectedId.value) {
        inputValue.value = Array.isArray(selectedId.value) ? selectedId.value.map(id => getLabelForValue(id)).join(', ') : getLabelForValue(selectedId.value)
    }
    
    emit('blur', blurContext)
}

const selectedAvatar = computed(() => {
    if (!props.showSelectedImage) return undefined
    const item = selectedItems.value?.[0]
    if (!item) return undefined
    if (item.showImage === false) return undefined
    const hasImage = Boolean(item.image)
    const hasInitials = Boolean(item.initials)
    if (!hasImage && !hasInitials && item.showImage !== true) return undefined
    return {
        image: item.image,
        initials: item.initials,
    }
})

watch([hasResults, () => props.isFetching], ([hasResultsValue, isFetching]) => {
    if (props.hideComboboxWhenEmpty && !hasResultsValue && !isFetching) {
        isOpen.value = false
    }
})

watch(allItems, (items) => {
    if (items.length > 0 && comboboxValue.value.length > 0 && (selectedItems.value?.length || 0) === 0) {
        const preselectedItems = comboboxValue.value
            .map(value => items.find(i => i.value === value))
            .filter((item): item is ComboboxItemData => item !== undefined)
        
        if (preselectedItems.length > 0) {
            selectedItems.value = preselectedItems
        }
    }
}, { immediate: true })

// Force repositioning when dropdown opens to fix SSR positioning issues
// Use requestAnimationFrame after nextTick to ensure lazy-mounted content is rendered
watch(isOpen, (newVal) => {
    if (newVal && typeof window !== 'undefined') {
        nextTick(() => {
            requestAnimationFrame(() => {
                window.dispatchEvent(new Event('resize'))
            })
        })
    }
})
</script>

<template>
    <div class="w-full">
        <Combobox.Root
            :positioning="{
                boundary: 'clippingAncestors',
                fitViewport: true,
                gutter: 8,
                sameWidth: true,
            }"
            class="w-full"
            :collection="collection"
            v-model="comboboxValue"
            :multiple="props.multiple"
            :disabled="isDisabled"
            :id="props.id || props.name"
            :name="props.name"
            v-model:open="isOpen"
            v-model:inputValue="inputValue"
            inputBehavior="autohighlight"
            :selectionBehavior="props.displayMode === 'combobox' && !props.multiple ? 'replace' : (props.mode === 'preserve' ? 'preserve' : 'clear')"
            :openOnClick="openOnClick"
            :openOnKeyPress="openOnKeyPress"
            @value-change="handleValueChange"
            :open-on-change="true"
            :on-input-value-change="(e) => console.log('inputValueChange', e)"
            :on-open-change="(e) => console.log('openChange', e)"
        >
            <UiAsyncComboboxControl
                :display-mode="props.displayMode"
                :multiple="props.multiple"
                :size="props.size"
                :is-invalid="isInvalid"
                :is-disabled="isDisabled"
                :is-open="isOpen"
                :placeholder="props.placeholder"
                :combobox-value="comboboxValue"
                :input-value="inputValue ?? ''"
                :described-by-id-base="describedByIdBase"
                :validation-error="validationError"
                :hint="props.hint"
                :get-label="getLabelForValue"
                :show-selected-image="props.showSelectedImage"
                :selected-avatar="selectedAvatar"
                @clear="clearInput"
                @remove="removeItem"
                @blur="handleBlur"
            >
                <template #leading-icon>
                    <slot name="leading-icon" />
                </template>
            </UiAsyncComboboxControl>

            <Teleport to="body" defer>
                <Combobox.Positioner class="!max-w-none">
                    <UiAsyncComboboxContent
                        :is-fetching-value="props.isFetching"
                        :has-results="hasResults"
                        :is-error-value="isErrorValue"
                        :is-grouped="isGrouped"
                        :grouped-data="groupedData"
                        :multiple="props.multiple"
                        :get-group-items="getGroupItems"
                        :get-link-props="getLinkProps"
                        :hide-combobox-when-empty="props.hideComboboxWhenEmpty"
                        :has-more="props.hasMore"
                        @load-more="emit('load-more')"
                    >
                        <template #loading>
                            <slot name="loading" />
                        </template>
                        <template #empty>
                            <slot name="empty" />
                        </template>
                        <template #group-heading="slotProps">
                            <slot name="group-heading" v-bind="slotProps" />
                        </template>
                    </UiAsyncComboboxContent>
                </Combobox.Positioner>
            </Teleport>
        </Combobox.Root>
        
        <!-- Chips below for inline mode multi-select -->
        <UiAsyncComboboxChips
            v-if="props.displayMode === 'inline' && props.multiple"
            :combobox-value="comboboxValue"
            :get-label="getLabelForValue"
            @remove="removeItem"
        />
        
        <div v-if="validationError || props.hint" class="flex flex-col mt-1">
            <UiText 
                v-if="validationError" 
                as="span" 
                variant="caption-normal" 
                class="text-se-danger"
                :id="`${describedByIdBase}-error`"
            >
                {{ validationError }}
            </UiText>
            <UiText 
                v-else-if="props.hint" 
                as="span" 
                variant="caption-normal" 
                class="text-se-gray-700"
                :id="`${describedByIdBase}-hint`"
            >
                {{ props.hint }}
            </UiText>
        </div>
    </div>
</template>
